/** @format */

import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  PostIdeaWrapper,
  showErrorToast,
  useAuthModal,
  useIsMobile,
} from "senf-atomic-design-system";
import { useCenterScrollZoom } from "senf-atomic-design-system/src/components/atoms/map/hooks/useCenterScrollZoom";
import { flyToPolygon } from "senf-atomic-design-system/src/components/atoms/map/utils/flyToPolygon";
import { useModals } from "senf-atomic-design-system/src/components/molecules/modalStack/ModalProvider";
import Success from "senf-atomic-design-system/src/components/organisms/modalContents/success/Success";
import {
  parseGeoData,
  postIdea,
  useAuthContext,
  useGlobalState,
  useSchemaValidation,
} from "senf-shared";
import { getIdea } from "../redux/actions/ideaActions";
import { EDIT_IDEA, POST_IDEA } from "../redux/types";

const PostIdeaPage = ({ handle, statefulMap, ideaMap }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { ideaId } = useParams();
  const { setModal, closeModal, openModal, closeDrawer } = useModals();
  const { user } = useAuthContext();
  const ideaData = useSelector((state) => state.data.idea);
  const project = useSelector((state) => state.data.project);
  const { openAuthModal } = useAuthModal(project);
  const [index, setIndex] = React.useState(0);
  const [isSubmitting, setSubmitting] = React.useState(false);
  const [statefulCesiumMap, setStatefulCesiumMap] =
    useGlobalState().statefulCesiumMap;

  const [location, setLocation] = useState({});
  const { validation } = useSchemaValidation();

  const addIdeaOptions = [
    ideaMap?.addIdeaOptions?.drawPoint && {
      icon: "pin",
      text: t("dialogMap:add.withLocationShort"),
      tab: "location",
    },
    ideaMap?.addIdeaOptions?.generalIdeas && {
      icon: "pin-strikethrough",
      text: t("dialogMap:add.withoutLocationShort"),
      tab: "noLocation",
    },
    ideaMap?.addIdeaOptions?.drawPath && {
      icon: "path",
      text: t("global:drawPath"),
      tab: "path",
      dataCy: "path",
    },
    ideaMap?.addIdeaOptions?.drawArea && {
      icon: "area",
      text: t("global:drawArea"),
      tab: "area",
      dataCy: "area",
    },
  ].filter(Boolean);
  const [geoDataType, setGeoDataType] = React.useState(
    addIdeaOptions?.[0]?.tab || "location",
  );

  useEffect(() => {
    // Only manage auth modal if we're on the post idea page
    if (pathname.includes("addIdea") || pathname.includes("postIdea")) {
      if (!user?.userId) {
        openAuthModal("login", {
          variant: "post",
          id: "authModal",
          onClose: () => {
            navigate(`${pathname.split("/").slice(0, -1).join("/")}`);
          },
          loggingInCallback: () => {
            closeDrawer({ id: "authModal" });
          },
        });
      } else {
        // Only close modal if it was opened by this page (with authModal id)
        // Don't interfere with modals opened by other components
        closeModal({ id: "authModal" });
      }
    }
  }, [user]);

  useEffect(() => {
    if (statefulMap) {
      if (pathname.includes("editIdea") && ideaId) {
        setIndex(1);
        if (ideaData?.long) {
          statefulMap.flyTo({
            center: [ideaData.long, ideaData.lat],
            zoom: 16.5,
            speed: 1,
            pitch: 30,
          });
        } else if (ideaData?.geoData) {
          flyToPolygon(statefulMap, parseGeoData(ideaData.geoData), {
            duration: 1000,
          });
        }
      }
    }
  }, [statefulMap, pathname, ideaId, ideaData]);

  const formik = useFormik({
    initialValues: {
      title: "",
      body: "",
      topic: "",
      address: "",
      contact: null,
      contactTitle: null,
      weblink: null,
      weblinkTitle: null,
    },
    validationSchema: validation([
      {
        body: t("global:description"),
        min: ideaMap?.addIdeaOptions?.description?.minChar ?? 50,
        max: ideaMap?.addIdeaOptions?.description?.maxChar ?? 800,
      },
    ]),
    validateOnChange: true,
    validateOnBlur: true,
  });

  useEffect(() => {
    if (pathname.indexOf("editIdea") > -1 && ideaId && !ideaData && project) {
      dispatch(getIdea(ideaId, project, ideaMap));
    }
  }, [ideaData, project, ideaMap, pathname]);

  useEffect(() => {
    if (ideaData && pathname.indexOf("editIdea") > -1) {
      setGeoDataType(ideaData?.geoDataType);
      const {
        comments,
        ideaId,
        userHandle,
        userImage,
        color,
        geoData,
        ...otherIdeaDataProperties
      } = ideaData;

      const newFormValues = { ...otherIdeaDataProperties };

      if (geoData && geoData !== "") {
        newFormValues.geoData = parseGeoData(geoData);
      }
      formik.setValues({ ...formik.values, ...newFormValues });
    }
  }, [ideaData, pathname, formik.setValues]);

  const handleSubmit = (event) => {
    if (isSubmitting) return;
    event?.preventDefault?.();
    setSubmitting(true);

    const map =
      ideaMap?.map?.mapStyle === "cesium" ? statefulCesiumMap : statefulMap;

    postIdea(formik, project, user, location, geoDataType, ideaId, map, ideaMap)
      .then((data) => {
        if (ideaId) data.ideaId = ideaId;
        dispatch({
          type: ideaId ? EDIT_IDEA : POST_IDEA,
          payload: data,
        });
        handle.openAddIdea(false);
        setIndex(0);
        setSubmitting(false);
        formik.resetForm();

        if (ideaId) {
          navigate(`${pathname.split("/").slice(0, -1).join("/")}`);
        } else {
          navigate(
            `${pathname.split("/").slice(0, -1).join("/")}/${data.ideaId}`,
          );
        }
        if (!ideaId) {
          setModal(
            <Success
              project={project}
              ideaMap={ideaMap}
              data={data}
              handle={handle}
              navigate={navigate}
            />,
            {
              id: "successModal",
              swipe: !!isMobile,
            },
          );
        }
      })
      .catch((error) => {
        console.error("Full error object:", error);
        console.error("Error message:", error.message);
        console.error("Error code:", error.code);
        console.error("Error name:", error.name);
        console.error("Error toString:", error.toString());
        console.error("Error stack:", error.stack);

        // Show user-friendly error toast
        const errorMessage = error.message?.toLowerCase() || "";
        const errorString = error.toString()?.toLowerCase() || "";

        console.log("Checking for network error patterns in:", errorMessage);
        console.log("Error string:", errorString);

        const isNetworkError =
          // Specific offline error pattern we found
          errorMessage.includes("request failed with error: undefined") ||
          // Firebase/Firestore error codes that might be in the message
          errorMessage.includes("unavailable") ||
          errorMessage.includes("deadline-exceeded") ||
          errorMessage.includes("network-request-failed") ||
          errorMessage.includes("permission-denied") ||
          // General network error indicators
          errorMessage.includes("offline") ||
          errorMessage.includes("network") ||
          errorMessage.includes("failed to fetch") ||
          errorMessage.includes("networkerror") ||
          errorMessage.includes("connection") ||
          errorMessage.includes("timeout") ||
          errorMessage.includes("fetch") ||
          errorMessage.includes("cors") ||
          errorMessage.includes("inet") ||
          // Check in error string as well
          errorString.includes("unavailable") ||
          errorString.includes("network") ||
          errorString.includes("offline") ||
          errorString.includes("connection") ||
          errorString.includes("timeout") ||
          errorString.includes("fetch") ||
          errorString.includes("request failed with error: undefined") ||
          // Error types
          error.name === "NetworkError" ||
          error.name === "TypeError" ||
          // Check original error properties if they exist
          error.code === "unavailable" ||
          error.code === "deadline-exceeded" ||
          error.code === "network-request-failed" ||
          error.code === "permission-denied";

        if (isNetworkError) {
          console.log("Detected as network error");
          showErrorToast(t("errors:networkError"));
        } else {
          console.log("Detected as generic submit error");
          showErrorToast(t("dialogMap:add.submitError"));
        }

        // Keep loading for a bit longer to provide visual feedback, then reset
        setTimeout(() => {
          setSubmitting(false);
        }, 1500);
      });
  };

  useCenterScrollZoom(statefulMap, {
    enabled: geoDataType === "location",
  });

  return (
    <PostIdeaWrapper
      handle={handle}
      addIdeaOptions={addIdeaOptions}
      statefulMap={statefulMap}
      statefulCesiumMap={statefulCesiumMap}
      project={project}
      ideaMap={ideaMap}
      geoDataType={geoDataType}
      formik={formik}
      setLocation={setLocation}
      pathname={pathname}
      handleSubmit={handleSubmit}
      setGeoDataType={setGeoDataType}
      index={index}
      setIndex={setIndex}
      isSubmitting={isSubmitting}
      ideaId={ideaId}
      variant="mainApp"
    />
  );
};

export default PostIdeaPage;
